// Import all your analytics event types
import AccountCreated from './account-created';
import AccountLogin from './account-login';
import ElementClicked from './element-clicked';
import ElementViewed from './element-viewed';
import FormSubmitted from './form-submitted';
import PageMetadata from './page-metadata';
import PortalSectionViewed from './portal-section-viewed';
import ProfileListViewed from './profile-list-viewed';

/**
 * @typedef {'page' | 'none'} DeduplicationScope
 */

/**
 * @typedef {Object} DeduplicationConfig
 * @property {DeduplicationScope} scope - When to deduplicate ('page' or 'none')
 * @property {(params: any) => string} [keyBuilder] - Function to build unique key from params
 */

/**
 * @typedef {Object} Event
 * @property {string} eventName - Event name
 * @property {'view' | 'link'} [tealiumMethodType='link'] - Tealium method to use
 * @property {DeduplicationConfig} [deduplication] - Deduplication configuration
 * @property {Function} createEventPayload - Function to create event instances
 * @property {Function} convertToTealiumFormat - Transform function (app data → Tealium format)
 * @property {Function} validateTealiumPayload - Validation function
 */

// Keep a list of all event modules for validation
/** @type {Event[]} */
const eventModules = [
  AccountCreated,
  AccountLogin,
  ElementClicked,
  ElementViewed,
  FormSubmitted,
  PageMetadata,
  PortalSectionViewed,
  ProfileListViewed,
];

/**
 * Event name constants - use these in your code instead of strings
 * @type {Record<string, string>}
 * @example
 * if (eventType === EventTypes.ELEMENT_CLICKED)
 */
export const EventTypes = {
  /** NOTE: These are explicitly listed so JSDoc intellisense works in IDEs */
  ACCOUNT_LOGIN: AccountLogin.eventName,
  ACCOUNT_CREATED: AccountCreated.eventName,
  ELEMENT_CLICKED: ElementClicked.eventName,
  ELEMENT_VIEWED: ElementViewed.eventName,
  FORM_SUBMITTED: FormSubmitted.eventName,
  PAGE_METADATA: PageMetadata.eventName,
  PORTAL_SECTION_VIEWED: PortalSectionViewed.eventName,
  PROFILE_LIST_VIEWED: ProfileListViewed.eventName,
};

/**
 * Event configuration lookup
 * Maps event name → { transform, validate, method, schema }
 * @type {Record<string, Event>}
 */
export const EventMap = {
  /** NOTE: These are explicitly listed so JSDoc intellisense works in IDEs */
  [AccountCreated.eventName]: AccountCreated,
  [AccountLogin.eventName]: AccountLogin,
  [ElementClicked.eventName]: ElementClicked,
  [ElementViewed.eventName]: ElementViewed,
  [FormSubmitted.eventName]: FormSubmitted,
  [PageMetadata.eventName]: PageMetadata,
  [PortalSectionViewed.eventName]: PortalSectionViewed,
  [ProfileListViewed.eventName]: ProfileListViewed,
};

/**
 * Easy event creators - use these in your React components
 */
export const EventCreators = {
  /** NOTE: These are explicitly listed so JSDoc intellisense works in IDEs */
  accountCreated: AccountCreated.createEventPayload,
  accountLogin: AccountLogin.createEventPayload,
  elementClicked: ElementClicked.createEventPayload,
  elementViewed: ElementViewed.createEventPayload,
  formSubmitted: FormSubmitted.createEventPayload,
  pageMetadata: PageMetadata.createEventPayload,
  portalSectionViewed: PortalSectionViewed.createEventPayload,
  profileListViewed: ProfileListViewed.createEventPayload,
};

/* #region development helpers */

/**
 * Development Safety Checks
 *
 * These run in development mode to catch common mistakes:
 * - Missing exports from event modules
 * - Forgetting to add new events to the registry
 * - Broken event configurations
 *
 * This prevents production bugs where events silently fail
 */
if (process.env.NODE_ENV !== 'production') {
  eventModules.forEach((module) => {
    // Check each event module has required exports
    if (
      !module.eventName ||
      !module.createEventPayload ||
      !module.convertToTealiumFormat ||
      !module.validateTealiumPayload
    ) {
      console.error(
        `❌ Cohesion Event module missing required exports:`,
        module,
      );
    }

    // Check event is registered in EventTypes
    const inEventTypes = Object.values(EventTypes).includes(module.eventName);
    if (!inEventTypes) {
      console.error(
        `❌ Cohesion Event "${module.eventName}" not found in \`EventTypes\`. Add it!`,
      );
    }

    // Check event is registered in EventMap
    const inEventMap = module.eventName in EventMap;
    if (!inEventMap) {
      console.error(
        `❌ Cohesion Event "${module.eventName}" not found in \`EventMap\`. Add it!`,
      );
    }

    // Check event has helper function in Events
    const moduleHelpers = Object.values(EventCreators).filter(
      (helper) => helper === module.createEventPayload,
    );
    if (moduleHelpers.length === 0) {
      console.error(
        `❌ Cohesion Event "${module.eventName}" helper not found in \`Events\`. Add createEventPayload function!`,
      );
    }
  });
}

/* #endregion */
